import { ValueFormatterParams } from "ag-grid-community";
import React, { useCallback, useState, useRef } from "react";
import Grid from "../../../../components/Grid";
import { ColumnDef, GridHandle } from "../../../../components/Grid/model/Grid";
import ModalDelete from "../../../../components/ModalDelete";
import Loading from "../../../../core/common/Loading";
import { formatDateTimeBR } from "../../../../helpers/format";
import api from "../../../../services/api";
import { ICustoEstadia } from "./types/types";
import PlusButtonIcon from "../../../../assets/images/PlusButtonIcon.svg";
import { useModal } from "../../../../hooks/ModalContext";
import Create from "./Create";
import Info from "./Info";
import { FrontendNotification } from "../../../../shared/Notification";
import Can from "../../../../components/Can";

const CustoEstadia: React.FC = () => {
  const [selectedRow, setSelectedRow] = useState<ICustoEstadia | undefined>();
  const [isRemove, setIsRemove] = useState<boolean>(false);
  const [isView, setIsView] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [customFilters, setCustomFilters] = useState<Record<string, unknown>>({ com_transportadora: false });
  const [gridKeyCounter, setGridKeyCounter] = useState<number>(0);
  const gridRef = useRef<GridHandle>(null);
  const { openModal, isModalOpen, closeModal } = useModal();
  const [activeFilter, setActiveFilter] = useState<'padrao' | 'negociado' | undefined>('padrao');

  // Definição dinâmica das colunas, agora após activeFilter
  const columns: ColumnDef[] = [
    ...(activeFilter === 'negociado' ? [
      {
        field: "razao_social",
        headerName: "Transportadora",
        filter: true,
        width: 180,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value !== undefined && params.value !== null) return String(params.value);
          return "-";
        },
      },
    ] : []),
    {
      field: "custo_hora",
      headerName: "Custo Hora (R$)",
      filter: true,
      type: "numberColumn",
      width: 180,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value !== undefined && params.value !== null) {
          const v = Number(params.value);
          return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
        }
        return "-";
      },
    },
    {
      field: "custo_meia_diaria",
      headerName: "Custo Meia Diária (R$)",
      filter: true,
      type: "numberColumn",
      width: 180,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value !== undefined && params.value !== null) {
          const v = Number(params.value);
          return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
        }
        return "-";
      },
    },
    {
      field: "custo_diaria",
      headerName: "Custo Diária (R$)",
      filter: true,
      type: "numberColumn",
      width: 180,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value !== undefined && params.value !== null) {
          const v = Number(params.value);
          return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
        }
        return "-";
      },
    },
    {
      field: "usuario",
      headerName: "Usuário",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "data_historico",
      headerName: "Data Modificação",
      filter: true,
      filterParams: {
        dateBetween: true,
      },
      width: 200,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return formatDateTimeBR(params.value as string);
        return "-";
      },
    },
  ];

  const onDelete = useCallback(async (entityId?: number) => {
    try {
      if (!entityId) return;
      setLoading(true);
      const body = { id_custo_estadia: entityId };
      await api.post("/deletar/custoEstadia", body);
      setLoading(false);
      setIsRemove(false);
      gridRef.current?.refresh();
    } catch {
      setLoading(false);
    }
  }, []);

  return (
    <>
      <Loading loading={loading} />

      {isRemove && (
        <ModalDelete
          onCancel={() => setIsRemove(!isRemove)}
          onConfirm={() => onDelete(selectedRow?.id_custo_estadia)}
          row={String(selectedRow?.id_custo_estadia ?? "") }
        />
      )}

      {isModalOpen && (
        <Create
          isEdit={isEdit}
          selectedRow={selectedRow}
          onClear={() => {
            closeModal();
            setIsEdit(false);
            setSelectedRow(undefined);
          }}
          onConfirm={() => {
            closeModal();
            setIsEdit(false);
            setSelectedRow(undefined);
            gridRef.current?.refresh();
          }}
        />
      )}

      {isView && (
        <Info
          data={selectedRow}
          title="Conhecer - Custo Estadia"
          onClose={() => {
            setIsView(false);
            setSelectedRow(undefined);
          }}
        />
      )}

      <div className="flex flex-col w-full h-screen bg-[#F5F5F5] p-5">
        <div className="flex items-center justify-between w-full mb-3">
          <div>
            <h1 className="text-2xl text-[#000000] font-bold">Custo Estadia</h1>
          </div>
          <div className="mr-14 flex items-center">
            <button
              className={
                `flex items-center justify-center h-10 px-4 text-sm font-light rounded-full mr-3 ` +
                (activeFilter === 'padrao'
                  ? 'bg-[#062D4E] text-white border-none'
                  : 'bg-white border border-[#CCCCCC] text-[#062D4E]')
              }
              onClick={() => {
                // activate padrao filter (do not allow deselecting)
                if (activeFilter === 'padrao') {
                  // already selected -> do nothing
                  return;
                }

                setActiveFilter('padrao');
                setCustomFilters({ com_transportadora: false });
                setGridKeyCounter(c => c + 1);
                FrontendNotification('Exibindo custos padrão', 'info');
              }}
              id="btnPadrao"
            >
              Padrão
            </button>

            <button
              className={
                `flex items-center justify-center h-10 px-4 text-sm font-light rounded-full mr-6 ` +
                (activeFilter === 'negociado'
                  ? 'bg-[#062D4E] text-white border-none'
                  : 'bg-white border border-[#062D4E] text-[#062D4E]')
              }
              onClick={() => {
                // activate negociado filter (do not allow deselecting)
                if (activeFilter === 'negociado') {
                  // already selected -> do nothing
                  return;
                }

                setActiveFilter('negociado');
                // backend expects 'com_transportadora' to indicate records with transportadora
                setCustomFilters({ com_transportadora: true });
                setGridKeyCounter(c => c + 1);
                FrontendNotification('Exibindo custos negociados', 'info');
              }}
              id="btnNegociado"
            >
              Negociado
            </button>

            <Can acao="SALVAR">
            <button
              className="flex items-center justify-center h-12 w-36 bg-[#062D4E] text-[#FFFFFF] text-sm font-light border-none rounded-full"
              onClick={() => openModal()}
              id="btnCreate"
            >
              Adicionar <img src={PlusButtonIcon} alt="" className="ml-2" />
            </button>
            </Can>
          </div>
        </div>

        <div className="flex w-full h-full">
          <Grid
            key={`${JSON.stringify(customFilters)}-${gridKeyCounter}`}
            ref={gridRef}
            columns={columns}
            filters={[]}
            customFilters={customFilters}
            pagination
            path="/listar/custoEstadia"
            onUpdate={(data: ICustoEstadia) => {
              setSelectedRow(data);
              setIsEdit(true);
              openModal();
            }}
            onDelete={(data: ICustoEstadia) => {
              setIsRemove(true);
              setSelectedRow(data);
            }}
            onView={(data: ICustoEstadia) => {
              setSelectedRow(data);
              setIsView(true);
            }}
            status={[]}
          />
        </div>
      </div>
    </>
  );
};

export default CustoEstadia;
